import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { upgradeToCookiePersistence, optOutOfCapturing } from '../utils/posthog';

function CookieConsentBar() {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Check if user has already given cookie consent
    const consentGiven = localStorage.getItem('cookieConsentGiven');
    const consentVersion = localStorage.getItem('cookieConsentVersion');
    const currentVersion = '1.0'; // Update this when you change cookie policy

    if (!consentGiven || consentVersion !== currentVersion) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      // Record cookie consent in database
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch('/api/cookie-consent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            consent_given: true,
            consent_version: '1.0',
            consent_type: 'all_cookies'
          })
        });

        if (!response.ok) {
          console.error('Failed to record cookie consent');
        }
      }

      // Store consent locally
      localStorage.setItem('cookieConsentGiven', 'true');
      localStorage.setItem('cookieConsentVersion', '1.0');
      localStorage.setItem('cookieConsentType', 'all_cookies');
      localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());

      // Upgrade PostHog to cookie persistence (was running in memory)
      if (process.env.NODE_ENV === 'production' || localStorage.getItem('testPostHog') === 'true') {
        upgradeToCookiePersistence();
      }

      setIsVisible(false);
    } catch (error) {
      console.error('Error recording cookie consent:', error);
      // Still hide the bar and upgrade PostHog even if database recording fails
      localStorage.setItem('cookieConsentGiven', 'true');
      localStorage.setItem('cookieConsentVersion', '1.0');
      localStorage.setItem('cookieConsentType', 'all_cookies');
      localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());
      if (process.env.NODE_ENV === 'production' || localStorage.getItem('testPostHog') === 'true') {
        upgradeToCookiePersistence();
      }
      setIsVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setIsSubmitting(true);
    try {
      // Record cookie consent in database (essential only)
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch('/api/cookie-consent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            consent_given: false, // Essential only
            consent_version: '1.0',
            consent_type: 'essential_only'
          })
        });

        if (!response.ok) {
          console.error('Failed to record cookie consent');
        }
      }

      // Store consent locally
      localStorage.setItem('cookieConsentGiven', 'false');
      localStorage.setItem('cookieConsentVersion', '1.0');
      localStorage.setItem('cookieConsentType', 'essential_only');
      localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());

      // Opt out of PostHog capturing (was running in memory)
      if (process.env.NODE_ENV === 'production' || localStorage.getItem('testPostHog') === 'true') {
        optOutOfCapturing();
      }

      setIsVisible(false);
    } catch (error) {
      console.error('Error recording cookie consent:', error);
      localStorage.setItem('cookieConsentGiven', 'false');
      localStorage.setItem('cookieConsentVersion', '1.0');
      localStorage.setItem('cookieConsentType', 'essential_only');
      localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());
      if (process.env.NODE_ENV === 'production' || localStorage.getItem('testPostHog') === 'true') {
        optOutOfCapturing();
      }
      setIsVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Just hide without recording consent
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-tint shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Content */}
          <div className="flex-1">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold">🍪</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-text mb-1">
                  Privacy & Analytics Settings
                </h3>
                <p className="text-sm text-textSecondary mb-2">
                  We use local storage for essential app functionality and optional analytics to help us improve our service.
                  You can choose to disable analytics tracking while keeping essential features.
                </p>
                <div className="text-xs text-textSecondary">
                  <strong>Essential storage:</strong> Required for login and session functionality<br />
                  <strong>Analytics tracking:</strong> Help us understand how you use our app (optional)
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={handleDecline}
              className="px-4 py-2 text-sm text-textSecondary hover:text-text transition-colors"
            >
              Essential Only
            </button>
            <button
              onClick={handleAccept}
              disabled={isSubmitting}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                isSubmitting
                  ? 'bg-primary/40 text-white cursor-not-allowed'
                  : 'bg-accent text-white hover:bg-accentDark'
              }`}
            >
              {isSubmitting ? 'Saving...' : 'Accept All'}
            </button>
            <button
              onClick={handleClose}
              className="p-2 text-textSecondary hover:text-text transition-colors"
              title="Close"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieConsentBar;
